import React, {useEffect, useRef, useState} from 'react';
import {
    AreaSeries,
    ColorType,
    createChart,
    IChartApi,
    ISeriesApi,
    LineSeries,
    LineStyle,
    UTCTimestamp,
} from 'lightweight-charts';
import {EquityPoint} from '../../services/botService';

// The parent status cards are fixed at 300px; leave space for the Tab bar and legend.
const CHART_HEIGHT_DESKTOP = 228;
const CHART_HEIGHT_MOBILE = 210;
const RECONCILIATION_EPSILON = 0.01;

interface EquityBreakdownChartsProps {
    points: EquityPoint[];
    pnlIncludedInEquity?: boolean;
    quality?: string;
    dexNoBreakdown?: boolean;
    showNonPnlBaseline?: boolean;
    onToggleNonPnlBaseline?: (visible: boolean) => void;
}

interface StackedPoint {
    point: EquityPoint;
    baseEquity: number;
    stablecoin: number;
    stablecoinAndOther: number;
    reconciliation: number;
}

const EquityBreakdownCharts: React.FC<EquityBreakdownChartsProps> = ({
                                                                         points,
                                                                         dexNoBreakdown,
                                                                         showNonPnlBaseline = true,
                                                                         onToggleNonPnlBaseline,
                                                                     }) => {
    const [isMobile, setIsMobile] = useState(false);
    const [chartReady, setChartReady] = useState(false);
    const [seriesVisibility, setSeriesVisibility] = useState({
        totalEquity: true,
        stablecoinEquity: true,
        otherAssetEquity: true,
        reconciliation: true,
    });

    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const totalEquitySeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
    const stablecoinSeriesRef = useRef<ISeriesApi<'Area'> | null>(null);
    const otherAssetSeriesRef = useRef<ISeriesApi<'Area'> | null>(null);
    const reconciliationSeriesRef = useRef<ISeriesApi<'Area'> | null>(null);
    const baseEquitySeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
    const pointsRef = useRef(points);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const prevTooltipTimeRef = useRef<number>(0);

    useEffect(() => {
        pointsRef.current = points;
    }, [points]);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        setIsMobile(mq.matches);
        const handler = (event: MediaQueryListEvent) => setIsMobile(event.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const chartHeight = isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT_DESKTOP;

    useEffect(() => {
        const container = chartContainerRef.current;
        if (!container) return;

        const chart = createChart(container, {
            width: Math.max(container.clientWidth, 300),
            height: chartHeight,
            layout: {
                background: {type: ColorType.Solid, color: '#0d1117'},
                textColor: '#999999',
                attributionLogo: false,
            },
            grid: {vertLines: {color: '#2a2a2a'}, horzLines: {color: '#2a2a2a'}},
            crosshair: {mode: 1},
            timeScale: {timeVisible: true, secondsVisible: true, borderVisible: false},
            rightPriceScale: {borderVisible: false},
            handleScroll: true,
            handleScale: true,
        });

        // Areas use cumulative values because Lightweight Charts has no native stacked series.
        const stableSeries = chart.addSeries(AreaSeries, {
            lineColor: '#4d9fff', lineWidth: 1,
            topColor: 'rgba(24, 144, 255, 0.34)', bottomColor: 'rgba(24, 144, 255, 0.06)',
            priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        });
        const otherSeries = chart.addSeries(AreaSeries, {
            lineColor: '#8C8C8C', lineWidth: 1,
            topColor: 'rgba(140, 140, 140, 0.28)', bottomColor: 'rgba(140, 140, 140, 0.01)',
            priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        });
        const reconciliationSeries = chart.addSeries(AreaSeries, {
            lineColor: '#FAAD14', lineWidth: 1,
            topColor: 'rgba(250, 173, 20, 0.22)', bottomColor: 'rgba(250, 173, 20, 0.01)',
            priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        });
        const baseEquitySeries = chart.addSeries(LineSeries, {
            color: '#BFBFBF', lineWidth: 1, lineStyle: LineStyle.Dashed,
            priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        });
        const totalEqSeries = chart.addSeries(LineSeries, {
            color: '#F0B429', lineWidth: 2,
            priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: true,
            crosshairMarkerRadius: 4,
        });

        chartRef.current = chart;
        totalEquitySeriesRef.current = totalEqSeries;
        stablecoinSeriesRef.current = stableSeries;
        otherAssetSeriesRef.current = otherSeries;
        reconciliationSeriesRef.current = reconciliationSeries;
        baseEquitySeriesRef.current = baseEquitySeries;
        setChartReady(true);

        chart.subscribeCrosshairMove((param: any) => {
            const tooltip = tooltipRef.current;
            if (!tooltip || !param.time || !param.point || typeof param.time !== 'number') {
                if (tooltip) tooltip.style.display = 'none';
                return;
            }
            const candidates = pointsRef.current;
            const time = param.time * 1000;
            const closest = candidates.reduce<EquityPoint | undefined>((current, candidate) =>
                    !current || Math.abs(candidate.time - time) < Math.abs(current.time - time) ? candidate : current,
                undefined);
            if (!closest || Math.abs(closest.time - time) > 120000) {
                tooltip.style.display = 'none';
                return;
            }
            if (prevTooltipTimeRef.current === closest.time) return;
            prevTooltipTimeRef.current = closest.time;

            const baseEquity = closest.totalEquity - (closest.pnlIncludedInEquity ? (closest.unsettledPnl ?? 0) : 0);
            const stablecoin = closest.stablecoinEquity ?? 0;
            const otherAsset = closest.otherAssetEquity ?? 0;
            const reconciliation = baseEquity - stablecoin - otherAsset;
            const date = new Date(closest.time);
            const timestamp = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}.${String(date.getMilliseconds()).padStart(3, '0')}`;
            const money = (value: number) => `$${value.toFixed(2)}`;
            const pnl = closest.unsettledPnl;
            const pnlColor = (pnl ?? 0) >= 0 ? '#00d97e' : '#ff5252';
            tooltip.innerHTML = `
                <div style="color:#999;margin-bottom:2px;font-size:10px">${timestamp}</div>
                <div style="color:#F0B429">总权益: ${money(closest.totalEquity)}</div>
                <div style="color:#BFBFBF">非盈亏基准: ${money(baseEquity)}</div>
                ${closest.stablecoinEquity != null ? `<div style="color:#4d9fff">稳定币: ${money(stablecoin)}</div>` : ''}
                ${closest.otherAssetEquity != null ? `<div style="color:#8C8C8C">其他资产: ${money(otherAsset)}</div>` : ''}
                ${Math.abs(reconciliation) >= RECONCILIATION_EPSILON ? `<div style="color:#FAAD14">其他权益（调节）: ${money(reconciliation)}</div>` : ''}
                <div>未结盈亏: <span style="color:${pnlColor}">${pnl == null ? '--' : `${pnl >= 0 ? '+' : ''}${money(pnl)}`}</span> <span style="color:#666;font-size:9px">(${closest.pnlIncludedInEquity ? '已计入总权益' : '未计入总权益'})</span></div>`;
            tooltip.style.display = 'block';
            tooltip.style.left = `${Math.min(param.point.x + 16, Math.max(container.clientWidth - 245, 0))}px`;
            tooltip.style.top = `${Math.max(param.point.y - 138, 0)}px`;
        });

        const resizeObserver = new ResizeObserver(() => {
            if (container.clientWidth) chart.applyOptions({width: container.clientWidth});
        });
        resizeObserver.observe(container);
        return () => {
            resizeObserver.disconnect();
            chart.remove();
            chartRef.current = null;
            totalEquitySeriesRef.current = null;
            stablecoinSeriesRef.current = null;
            otherAssetSeriesRef.current = null;
            reconciliationSeriesRef.current = null;
            baseEquitySeriesRef.current = null;
            setChartReady(false);
        };
    }, [chartHeight]);

    useEffect(() => {
        chartRef.current?.applyOptions({height: chartHeight});
    }, [chartHeight]);

    useEffect(() => {
        if (!chartReady) return;
        const dedup = <T extends { time: UTCTimestamp }>(items: T[]) =>
            items.filter((item, index, values) => index === values.length - 1 || item.time !== values[index + 1].time);
        const sorted = [...points].sort((a, b) => a.time - b.time);
        const stackedPoints: StackedPoint[] = sorted
            .filter(point => point.stablecoinEquity != null || point.otherAssetEquity != null)
            .map(point => {
                const baseEquity = point.totalEquity - (point.pnlIncludedInEquity ? (point.unsettledPnl ?? 0) : 0);
                const stablecoin = point.stablecoinEquity ?? 0;
                const otherAsset = point.otherAssetEquity ?? 0;
                return {
                    point,
                    baseEquity,
                    stablecoin,
                    stablecoinAndOther: stablecoin + otherAsset,
                    reconciliation: baseEquity - stablecoin - otherAsset
                };
            });

        totalEquitySeriesRef.current?.setData(dedup(sorted.map(point => ({
            time: (point.time / 1000) as UTCTimestamp,
            value: point.totalEquity
        }))));
        baseEquitySeriesRef.current?.setData(dedup(stackedPoints.map(({
                                                                          point,
                                                                          baseEquity
                                                                      }) => ({
            time: (point.time / 1000) as UTCTimestamp,
            value: baseEquity
        }))));
        stablecoinSeriesRef.current?.setData(dedup(stackedPoints.map(({
                                                                          point,
                                                                          stablecoin
                                                                      }) => ({
            time: (point.time / 1000) as UTCTimestamp,
            value: stablecoin
        }))));
        otherAssetSeriesRef.current?.setData(dedup(stackedPoints.map(({
                                                                          point,
                                                                          stablecoinAndOther
                                                                      }) => ({
            time: (point.time / 1000) as UTCTimestamp,
            value: stablecoinAndOther
        }))));
        reconciliationSeriesRef.current?.setData(dedup(stackedPoints.map(({
                                                                              point,
                                                                              baseEquity
                                                                          }) => ({
            time: (point.time / 1000) as UTCTimestamp,
            value: baseEquity
        }))));

        const hasReconciliation = stackedPoints.some(({reconciliation}) => Math.abs(reconciliation) >= RECONCILIATION_EPSILON);
        totalEquitySeriesRef.current?.applyOptions({visible: seriesVisibility.totalEquity});
        stablecoinSeriesRef.current?.applyOptions({visible: seriesVisibility.stablecoinEquity && stackedPoints.length > 0});
        otherAssetSeriesRef.current?.applyOptions({visible: seriesVisibility.otherAssetEquity && stackedPoints.length > 0});
        reconciliationSeriesRef.current?.applyOptions({visible: seriesVisibility.reconciliation && hasReconciliation});
        baseEquitySeriesRef.current?.applyOptions({visible: showNonPnlBaseline && stackedPoints.length > 0});
        chartRef.current?.timeScale().fitContent();
    }, [chartReady, points, seriesVisibility, showNonPnlBaseline]);

    const hasPnlData = points.some(point => point.unsettledPnl != null);
    const hasReconciliation = points.some(point => {
        if (point.stablecoinEquity == null && point.otherAssetEquity == null) return false;
        const base = point.totalEquity - (point.pnlIncludedInEquity ? (point.unsettledPnl ?? 0) : 0);
        return Math.abs(base - (point.stablecoinEquity ?? 0) - (point.otherAssetEquity ?? 0)) >= RECONCILIATION_EPSILON;
    });

    return (
        <div style={{position: 'relative'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4, flexWrap: 'wrap'}}>
                <LegendItem color="#F0B429" label="总权益" visible={seriesVisibility.totalEquity}
                            onClick={() => setSeriesVisibility(value => ({
                                ...value,
                                totalEquity: !value.totalEquity
                            }))}/>
                {!dexNoBreakdown && <>
                    <LegendItem color="#4d9fff" label="稳定币" visible={seriesVisibility.stablecoinEquity}
                                onClick={() => setSeriesVisibility(value => ({
                                    ...value,
                                    stablecoinEquity: !value.stablecoinEquity
                                }))}/>
                    <LegendItem color="#8C8C8C" label="其他资产" visible={seriesVisibility.otherAssetEquity}
                                onClick={() => setSeriesVisibility(value => ({
                                    ...value,
                                    otherAssetEquity: !value.otherAssetEquity
                                }))}/>
                    {hasReconciliation &&
                        <LegendItem color="#FAAD14" label="其他权益（调节）" visible={seriesVisibility.reconciliation}
                                    onClick={() => setSeriesVisibility(value => ({
                                        ...value,
                                        reconciliation: !value.reconciliation
                                    }))}/>}
                </>}
                <LegendItem color="#BFBFBF" label="非盈亏基准（总权益-未结算盈亏）" visible={showNonPnlBaseline} dashed
                            onClick={() => onToggleNonPnlBaseline?.(!showNonPnlBaseline)}/>
                {dexNoBreakdown && <span style={{color: '#666', fontSize: 10}}>钱包资产分项暂不可用</span>}
                {!hasPnlData && <span style={{color: '#666', fontSize: 10}}>未结算盈亏数据暂不可用</span>}
            </div>
            <div ref={chartContainerRef} style={{width: '100%', height: chartHeight, cursor: 'crosshair'}}/>
            <div ref={tooltipRef} style={{
                display: 'none',
                position: 'absolute',
                zIndex: 10,
                background: 'rgba(20, 20, 20, 0.94)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 4,
                padding: '8px 10px',
                fontSize: 11,
                lineHeight: '1.6',
                pointerEvents: 'none',
                whiteSpace: 'nowrap'
            }}/>
        </div>
    );
};

const LegendItem: React.FC<{
    color: string;
    label: string;
    visible: boolean;
    dashed?: boolean;
    onClick: () => void
}> = ({color, label, visible, dashed, onClick}) => (
    <span onClick={onClick}
          style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, opacity: visible ? 1 : 0.4}}>
        <span style={dashed ? {width: 14, borderTop: `2px dashed ${color}`} : {color, fontSize: 12}}>●</span>
        <span style={{color: '#999', fontSize: 11}}>{label}</span>
    </span>
);

export default EquityBreakdownCharts;
